import React, { useMemo, useState } from 'react';
import {
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Input,
  Textarea,
  Select,
  SelectItem,
  Chip,
} from '@nextui-org/react';
import { PenSquare, Plus } from 'lucide-react';
import forumService, {
  ForumCategory,
  ForumTopic,
  extractForumError,
} from '../../services/forumService';

const TEAL = '#3FCED2';

interface CreateTopicModalProps {
  isOpen: boolean;
  onClose: () => void;
  categories: ForumCategory[];
  selectedCategory?: ForumCategory | null;
  onCreated: (topic: ForumTopic) => void;
}

/** Kategorien (inkl. Unterkategorien) flach machen – nur Kategorien,
 *  in denen Topic-Erstellung erlaubt ist (Port aus Flutter). */
function flattenCategories(categories: ForumCategory[]): ForumCategory[] {
  const result: ForumCategory[] = [];
  for (const cat of categories) {
    if (cat.topicCreationEnabled !== false) result.push(cat);
    if (cat.subcategories && cat.subcategories.length > 0) {
      result.push(...flattenCategories(cat.subcategories));
    }
  }
  return result;
}

/**
 * "Neues Topic erstellen"-Modal – Port von forum_create_topic_modal.dart:
 * Kategorie-Auswahl, Titel (5-200 Zeichen), Inhalt (mind. 20 Zeichen),
 * optionale Tags (max. 5, lowercase).
 */
const CreateTopicModal: React.FC<CreateTopicModalProps> = ({
  isOpen,
  onClose,
  categories,
  selectedCategory,
  onCreated,
}) => {
  const [categoryId, setCategoryId] = useState<string>(
    selectedCategory?.id || selectedCategory?._id || '',
  );
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [tagInput, setTagInput] = useState('');
  const [tags, setTags] = useState<string[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const flatCategories = useMemo(
    () => flattenCategories(categories),
    [categories],
  );

  const reset = () => {
    setCategoryId(selectedCategory?.id || selectedCategory?._id || '');
    setTitle('');
    setContent('');
    setTagInput('');
    setTags([]);
    setError(null);
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const addTag = (raw: string) => {
    const trimmed = raw.trim().toLowerCase();
    if (trimmed.length > 0 && !tags.includes(trimmed) && tags.length < 5) {
      setTags((prev) => [...prev, trimmed]);
    }
    setTagInput('');
  };

  const removeTag = (tag: string) => {
    setTags((prev) => prev.filter((t) => t !== tag));
  };

  const handleSubmit = async () => {
    setError(null);

    // Validierungen – Port aus der Flutter-App
    if (!categoryId) {
      setError('Bitte wähle eine Kategorie aus.');
      return;
    }
    const trimmedTitle = title.trim();
    if (trimmedTitle.length < 5) {
      setError('Der Titel muss mindestens 5 Zeichen haben.');
      return;
    }
    if (trimmedTitle.length > 200) {
      setError('Der Titel darf maximal 200 Zeichen haben.');
      return;
    }
    const trimmedContent = content.trim();
    if (trimmedContent.length < 20) {
      setError('Der Inhalt muss mindestens 20 Zeichen haben.');
      return;
    }

    setSubmitting(true);
    try {
      const topic = await forumService.createTopic({
        categoryId,
        title: trimmedTitle,
        content: trimmedContent,
        tags: tags.length > 0 ? tags : undefined,
      });
      reset();
      onClose();
      onCreated(topic);
    } catch (err) {
      setError(extractForumError(err, 'Topic konnte nicht erstellt werden'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      size="2xl"
      scrollBehavior="inside"
      classNames={{
        base: 'bg-zinc-900 border border-zinc-800 text-white',
        closeButton: 'text-gray-400 hover:bg-zinc-800',
      }}
    >
      <ModalContent>
        <ModalHeader className="flex items-center gap-2">
          <PenSquare size={20} style={{ color: TEAL }} />
          Neues Topic erstellen
        </ModalHeader>
        <ModalBody className="gap-4">
          {/* Kategorie */}
          <Select
            label="Kategorie"
            placeholder="Kategorie auswählen..."
            selectedKeys={categoryId ? [categoryId] : []}
            onSelectionChange={(keys) => {
              const first = Array.from(keys)[0];
              setCategoryId(first ? String(first) : '');
            }}
            classNames={{
              trigger: 'bg-zinc-800 data-[hover=true]:bg-zinc-800',
              label: 'text-gray-400',
              value: 'text-white',
              popoverContent: 'bg-zinc-900 border border-zinc-800 text-white',
            }}
          >
            {flatCategories.map((cat) => (
              <SelectItem key={(cat.id || cat._id) as string}>
                {`${cat.icon ? `${cat.icon} ` : ''}${cat.name || 'Kategorie'}`}
              </SelectItem>
            ))}
          </Select>

          {/* Titel */}
          <Input
            label="Titel"
            placeholder="Ein aussagekräftiger Titel..."
            value={title}
            onValueChange={setTitle}
            maxLength={200}
            description={`${title.length}/200 Zeichen`}
            classNames={{
              inputWrapper: 'bg-zinc-800 data-[hover=true]:bg-zinc-800',
              input: 'text-white',
              label: 'text-gray-400',
              description: 'text-gray-500',
            }}
          />

          {/* Inhalt */}
          <Textarea
            label="Inhalt"
            placeholder="Worüber möchtest du diskutieren? (mind. 20 Zeichen)"
            value={content}
            onValueChange={setContent}
            minRows={6}
            maxLength={50000}
            classNames={{
              inputWrapper: 'bg-zinc-800 data-[hover=true]:bg-zinc-800',
              input: 'text-white',
              label: 'text-gray-400',
            }}
          />

          {/* Tags */}
          <div>
            <Input
              label="Tags (optional)"
              placeholder="Tag eingeben und Enter drücken..."
              value={tagInput}
              onValueChange={setTagInput}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  addTag(tagInput);
                }
              }}
              endContent={
                <button
                  type="button"
                  onClick={() => addTag(tagInput)}
                  className="text-gray-400 hover:text-white"
                  aria-label="Tag hinzufügen"
                >
                  <Plus size={18} />
                </button>
              }
              classNames={{
                inputWrapper: 'bg-zinc-800 data-[hover=true]:bg-zinc-800',
                input: 'text-white',
                label: 'text-gray-400',
              }}
            />
            {tags.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-2">
                {tags.map((tag) => (
                  <Chip
                    key={tag}
                    onClose={() => removeTag(tag)}
                    variant="flat"
                    className="bg-zinc-800 text-gray-300"
                  >
                    #{tag}
                  </Chip>
                ))}
              </div>
            )}
            <p className="text-xs text-gray-500 mt-1">
              Maximal 5 Tags, hilft anderen dein Topic zu finden
            </p>
          </div>

          {error && <p className="text-red-500 text-sm">{error}</p>}
        </ModalBody>
        <ModalFooter>
          <Button
            variant="flat"
            className="bg-zinc-800 text-gray-300"
            onPress={handleClose}
            isDisabled={submitting}
          >
            Abbrechen
          </Button>
          <Button
            style={{ backgroundColor: TEAL, color: '#000' }}
            className="font-semibold"
            onPress={handleSubmit}
            isLoading={submitting}
          >
            Topic veröffentlichen
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default CreateTopicModal;
